/**
 * RhHabilitationsPanel — habilitations & permis des agents (item 5.3).
 *
 * « Habilitations & permis (conduite, électrique, CACES…) : documents, dates de
 *   péremption, rappels paramétrables. »
 *
 * ─── CE QUE CET ÉCRAN SERT À FAIRE ──────────────────────────────────────────
 *
 * Répondre à une question : **qui ne peut plus conduire l'engin ?**
 *
 * Un agent dont le CACES est périmé ne peut légalement plus l'utiliser. La
 * commune engage sa responsabilité si elle le laisse faire. L'écran ouvre donc
 * sur ce qui est **périmé ou sur le point de l'être** — pas sur une liste
 * alphabétique où l'urgence se noierait.
 *
 * Les rappels partent tout seuls (moteur du lot N5). Cet écran sert à saisir et
 * à voir l'état, pas à surveiller.
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, Badge, Alert, Button, Field } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import type { RhHabilitation, RhHabilitationType, AdminUserListItem } from '@roronoa-collectivites/shared-types';
import { BadgeCheck, Loader2, Paperclip, Plus, Trash2, Pencil } from 'lucide-react';

/** L'état de péremption, en un coup d'œil. */
function EtatBadge({ h }: { h: RhHabilitation }) {
  if (h.etat === 'perime') {
    return <Badge variant="urgent">Périmée{h.joursRestants !== null ? ` depuis ${Math.abs(h.joursRestants)} j` : ''}</Badge>;
  }
  if (h.etat === 'bientot') {
    return <Badge variant="warning">Dans {h.joursRestants} j</Badge>;
  }
  if (h.etat === 'sans_echeance') {
    return <Badge variant="neutral">Sans échéance</Badge>;
  }
  return <Badge variant="success">Valide</Badge>;
}

export function RhHabilitationsPanel() {
  const { confirm, dialogElement } = useConfirmDialog();
  const [items, setItems] = useState<RhHabilitation[]>([]);
  const [types, setTypes] = useState<RhHabilitationType[]>([]);
  const [agents, setAgents] = useState<AdminUserListItem[]>([]);
  const [filtre, setFiltre] = useState<'a_renouveler' | 'tous'>('a_renouveler');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<RhHabilitation> & { fichier?: File } | null>(null);
  const [saving, setSaving] = useState(false);

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [h, t] = await Promise.all([
        api.rh.habilitations.list(filtre),
        api.rh.habilitations.types(),
      ]);
      setItems(h); setTypes(t.filter((x) => x.actif));
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible.');
    } finally { setLoading(false); }
  }, [filtre]);

  useEffect(() => { void charger(); }, [charger]);

  useEffect(() => {
    // La liste des agents alimente le sélecteur de saisie. Best-effort : si elle
    // échoue, l'écran reste consultable.
    void api.admin.users.list({ role: 'agent' })
      .then((r) => setAgents(r.items)).catch(() => { /* consultation seule */ });
  }, []);

  async function enregistrer() {
    if (!form?.agentUserId || !form.typeId) {
      setError('Choisissez un agent et un type.'); return;
    }
    setSaving(true); setError(null); setMsg(null);
    try {
      const payload = {
        agentUserId: form.agentUserId,
        typeId: form.typeId,
        reference: form.reference || undefined,
        dateObtention: form.dateObtention || undefined,
        dateEcheance: form.dateEcheance || undefined,
        notes: form.notes || undefined,
        rappelOffsets: form.rappelOffsets,
      };
      if (form.id) await api.rh.habilitations.update(form.id, payload, form.fichier);
      else await api.rh.habilitations.create(payload, form.fichier);
      setMsg(form.id ? 'Habilitation modifiée.' : 'Habilitation enregistrée.');
      setForm(null);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Enregistrement impossible.');
    } finally { setSaving(false); }
  }

  async function supprimer(h: RhHabilitation) {
    const ok = await confirm({
      title: 'Supprimer cette habilitation ?',
      description: `${h.agentNom} · ${h.typeLibelle}\n\nL'historique sera perdu, ainsi que les rappels programmés.`,
      confirmLabel: 'Supprimer', danger: true,
    });
    if (!ok) return;
    try {
      await api.rh.habilitations.remove(h.id);
      setMsg('Habilitation supprimée.');
      await charger();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Suppression impossible.'); }
  }

  async function ouvrirPiece(h: RhHabilitation) {
    try {
      const { url } = await api.rh.habilitations.piece(h.id);
      window.open(url, '_blank', 'noopener');
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Pièce indisponible.'); }
  }

  /** Pré-remplit l'échéance depuis la durée usuelle du type — corrigeable. */
  function onTypeChange(typeId: string) {
    const t = types.find((x) => x.id === typeId);
    if (!t?.dureeMois || !form?.dateObtention) { setForm({ ...form, typeId }); return; }
    const d = new Date(form.dateObtention);
    d.setMonth(d.getMonth() + t.dureeMois);
    // Formatage sur les composantes locales : pas de toISOString(), qui
    // décalerait d'un jour selon le fuseau.
    const echeance = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setForm({ ...form, typeId, dateEcheance: echeance });
  }

  const perimees = items.filter((h) => h.etat === 'perime').length;

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      {/* Ce qui compte vraiment : quelqu'un ne peut plus légalement conduire. */}
      {perimees > 0 && (
        <Alert variant="error">
          {perimees === 1
            ? 'Une habilitation est périmée : l’agent concerné ne peut plus effectuer la tâche correspondante.'
            : `${perimees} habilitations sont périmées : les agents concernés ne peuvent plus effectuer les tâches correspondantes.`}
        </Alert>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          {([['a_renouveler', 'À renouveler'], ['tous', 'Toutes']] as const).map(([k, lab]) => (
            <button key={k} onClick={() => setFiltre(k)}
              className={`rounded-r-pill border px-3.5 py-1.5 text-xs font-semibold transition ${
                filtre === k ? 'border-rh bg-rh-tint/60 text-rh-fg' : 'border-border-hi bg-surface/40 text-ink-muted hover:text-ink'}`}>
              {lab}
            </button>
          ))}
        </div>
        <Button variant="primary" size="sm" onClick={() => setForm({ rappelOffsets: [90, 30] })}>
          <Plus className="h-4 w-4" /> Ajouter
        </Button>
      </div>

      {form && (
        <Card className="p-4">
          <h3 className="mb-3 font-display text-sm font-semibold text-ink">
            {form.id ? 'Modifier l’habilitation' : 'Nouvelle habilitation'}
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Agent" htmlFor="hab-agent">
              <select id="hab-agent" value={form.agentUserId ?? ''}
                onChange={(e) => setForm({ ...form, agentUserId: e.target.value })}
                className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
                <option value="">Choisir…</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.prenom} {a.nom}</option>
                ))}
              </select>
            </Field>
            <Field label="Type" htmlFor="hab-type">
              <select id="hab-type" value={form.typeId ?? ''}
                onChange={(e) => onTypeChange(e.target.value)}
                className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
                <option value="">Choisir…</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>{t.libelle}</option>
                ))}
              </select>
            </Field>
            <Field label="Obtenue le" htmlFor="hab-obt">
              <input id="hab-obt" type="date" value={form.dateObtention ?? ''}
                onChange={(e) => setForm({ ...form, dateObtention: e.target.value })}
                className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
            </Field>
            <Field label="Expire le" htmlFor="hab-ech">
              <input id="hab-ech" type="date" value={form.dateEcheance ?? ''}
                onChange={(e) => setForm({ ...form, dateEcheance: e.target.value })}
                className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
            </Field>
            <Field label="Référence (n° de titre)" htmlFor="hab-ref">
              <input id="hab-ref" type="text" value={form.reference ?? ''}
                onChange={(e) => setForm({ ...form, reference: e.target.value })}
                className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
            </Field>
            <Field label="Document scanné" htmlFor="hab-file">
              <input id="hab-file" type="file" accept="image/*,application/pdf"
                onChange={(e) => setForm({ ...form, fichier: e.target.files?.[0] })}
                className="w-full text-xs text-ink-muted file:mr-2 file:rounded-r-md file:border-0 file:bg-surface2 file:px-3 file:py-2 file:text-xs file:text-ink" />
            </Field>
          </div>

          <div className="mt-3">
            <p className="mb-1.5 text-xs text-ink-muted">
              Prévenir combien de jours avant l’expiration ? Un recyclage se planifie.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {[180, 90, 60, 30, 15].map((j) => {
                const on = (form.rappelOffsets ?? []).includes(j);
                return (
                  <button key={j} type="button"
                    onClick={() => setForm({
                      ...form,
                      rappelOffsets: on
                        ? (form.rappelOffsets ?? []).filter((x) => x !== j)
                        : [...(form.rappelOffsets ?? []), j].sort((a, b) => b - a),
                    })}
                    className={`rounded-r-pill border px-3 py-1 text-xs transition ${
                      on ? 'border-rh bg-rh-tint/60 text-rh-fg' : 'border-border bg-surface/40 text-ink-muted'}`}>
                    {j} j
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setForm(null)}>Annuler</Button>
            <Button variant="primary" size="sm" onClick={() => void enregistrer()} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
            </Button>
          </div>
        </Card>
      )}

      <Card className="p-4">
        <h2 className="mb-3 flex items-center gap-2 font-display text-base font-semibold text-ink">
          <BadgeCheck className="h-4 w-4 text-rh" />
          {filtre === 'a_renouveler' ? 'À renouveler' : 'Toutes les habilitations'}
          {items.length > 0 && <Badge variant="rh">{items.length}</Badge>}
        </h2>

        {loading ? (
          <p className="text-sm text-ink-muted">Chargement…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-ink-subtle">
            {filtre === 'a_renouveler'
              ? 'Aucune habilitation à renouveler dans les 90 prochains jours.'
              : 'Aucune habilitation enregistrée.'}
          </p>
        ) : (
          <ul className="space-y-2">
            {items.map((h) => (
              <li key={h.id} className="flex flex-wrap items-center justify-between gap-2 rounded-r-md border border-border bg-surface/40 px-3 py-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-ink">{h.agentNom}</span>
                    <EtatBadge h={h} />
                  </div>
                  <div className="text-xs text-ink-muted">
                    {h.typeLibelle}
                    {h.reference && ` · n° ${h.reference}`}
                    {h.dateEcheance && ` · expire le ${h.dateEcheance}`}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {h.aPiece && (
                    <button type="button" onClick={() => void ouvrirPiece(h)}
                      title={h.nomFichier ?? 'Voir le document'}
                      className="text-ink-muted hover:text-rh">
                      <Paperclip className="h-4 w-4" />
                    </button>
                  )}
                  <button type="button" onClick={() => setForm(h)}
                    title="Modifier" className="text-ink-muted hover:text-ink">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={() => void supprimer(h)}
                    title="Supprimer" className="text-ink-muted hover:text-urgent">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {dialogElement}
    </div>
  );
}
